import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  HStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  SimpleGrid,
  Spinner,
  Flex,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  ButtonGroup,
  Image,
  useDisclosure,
} from '@chakra-ui/react';
import { FiEdit, FiArrowLeft, FiPlus } from 'react-icons/fi';
import { patientApi, visitApi, getStaticFileUrl, PatientsService } from '../services/api';
import VisitModal from './VisitModal';

function PatientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visitToEdit, setVisitToEdit] = useState(null);
  const [patientGoals, setPatientGoals] = useState([]);

  const fetchPatient = useCallback(async () => {
    try {
      setLoading(true);
      const response = await patientApi.getById(id);
      setPatient(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to fetch patient details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchVisits = useCallback(async () => {
    try {
      setVisitsLoading(true);
      const response = await visitApi.getAll(id); // Filter by patient ID
      setVisits(response.data);
    } catch (err) {
      console.error('Failed to fetch visits:', err);
    } finally {
      setVisitsLoading(false);
    }
  }, [id]);

  const fetchPatientGoals = useCallback(async () => {
    try {
      const response = await PatientsService.getPatientGoalsApiPatientsPatientIdGoalsGet({ patientId: id });
      setPatientGoals(response);
    } catch (err) {
      console.error('Failed to fetch patient goals:', err);
    }
  }, [id]);

  useEffect(() => {
    fetchPatient();
    fetchVisits();
    fetchPatientGoals();
  }, [fetchPatient, fetchVisits, fetchPatientGoals]);

  const handleAddVisit = () => {
    setVisitToEdit(null);
    onOpen();
  };

  const handleEditVisit = (visit) => {
    setVisitToEdit(visit);
    onOpen();
  };

  const handleDeleteVisit = async (visitId) => {
    if (window.confirm('Are you sure you want to delete this visit?')) {
      try {
        await visitApi.delete(visitId);
        fetchVisits(); // Refresh the list
      } catch (err) {
        console.error('Failed to delete visit:', err);
      }
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" h="400px">
        <Spinner size="xl" color="brand.purple" thickness="4px" />
      </Flex>
    );
  }

  if (error || !patient) {
    return (
      <Box>
        <Alert status="error" borderRadius="lg">
          <AlertIcon />
          {error || 'Patient not found'}
        </Alert>
        <Button mt={4} onClick={() => navigate('/app/patients')} leftIcon={<FiArrowLeft />}>
          Back to Patients
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <HStack spacing={4}>
          <Button
            variant="ghost"
            leftIcon={<FiArrowLeft />}
            onClick={() => navigate('/app/patients')}
          >
            Back
          </Button>
          <Box>
            <Heading size="lg">
              {patient.first_name} {patient.last_name}
            </Heading>
            <Text color="gray.600" fontSize="sm">
              Patient ID: {patient.id}
            </Text>
          </Box>
        </HStack>
        <Button
          as={RouterLink}
          to={`/app/patients/edit/${patient.id}`}
          leftIcon={<FiEdit />}
          variant="outline"
          colorScheme="purple"
        >
          Edit Patient
        </Button>
      </Flex>

      {/* Tabbed Content */}
      <Tabs colorScheme="purple" isLazy>
        <TabList>
          <Tab>Overview</Tab>
          <Tab>Visits ({visits.length})</Tab>
          <Tab isDisabled>Documents</Tab>
        </TabList>

        <TabPanels>
          {/* Overview Tab */}
          <TabPanel>
            {/* Basic Information */}
            <Card mb={4}>
              <CardBody>
                <Heading size="md" mb={4}>Basic Information</Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <VStack align="start" spacing={3}>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">
                        Date of Birth
                      </Text>
                      <Text fontSize="lg">{patient.date_of_birth}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">
                        Email
                      </Text>
                      <Text fontSize="lg">{patient.email || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">
                        Phone
                      </Text>
                      <Text fontSize="lg">{patient.phone || 'N/A'}</Text>
                    </Box>
                  </VStack>
                  <VStack align="start" spacing={3}>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">
                        Address
                      </Text>
                      <Text fontSize="lg">{patient.address || 'N/A'}</Text>
                    </Box>
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600">
                        Member Since
                      </Text>
                      <Text fontSize="lg">
                        {new Date(patient.created_at).toLocaleDateString()}
                      </Text>
                    </Box>
                  </VStack>
                </SimpleGrid>
              </CardBody>
            </Card>

            {/* Client Goals */}
            <Card mb={4}>
              <CardBody>
                <Heading size="md" mb={4} color="purple.700">Client Goals</Heading>
                {patientGoals.length > 0 || patient.other_goals ? (
                  <VStack align="stretch" spacing={3}>
                    {patientGoals.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={2}>
                          Selected Goals
                        </Text>
                        <Flex flexWrap="wrap" gap={2}>
                          {patientGoals.map((goal) => (
                            <Badge
                              key={goal.id}
                              colorScheme="purple"
                              fontSize="sm"
                              px={3}
                              py={1}
                              borderRadius="full"
                            >
                              {goal.name}
                            </Badge>
                          ))}
                        </Flex>
                      </Box>
                    )}
                    {patient.other_goals && (
                      <Box>
                        <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                          Other Goals
                        </Text>
                        <Text fontSize="md" whiteSpace="pre-wrap">{patient.other_goals}</Text>
                      </Box>
                    )}
                  </VStack>
                ) : (
                  <Text color="gray.500" fontStyle="italic">
                    No goals specified
                  </Text>
                )}
              </CardBody>
            </Card>

            {/* Medical History */}
            <Card>
              <CardBody>
                <Heading size="md" mb={4} color="purple.700">Medical History</Heading>
                <VStack align="stretch" spacing={4}>
                  {patient.allergies && (
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                        Allergies
                      </Text>
                      <Text fontSize="md" whiteSpace="pre-wrap">{patient.allergies}</Text>
                    </Box>
                  )}
                  {patient.current_medications && (
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                        Current Medications
                      </Text>
                      <Text fontSize="md" whiteSpace="pre-wrap">{patient.current_medications}</Text>
                    </Box>
                  )}
                  {patient.medical_conditions && (
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                        Medical Conditions
                      </Text>
                      <Text fontSize="md" whiteSpace="pre-wrap">{patient.medical_conditions}</Text>
                    </Box>
                  )}
                  {patient.previous_surgeries && (
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                        Previous Surgeries
                      </Text>
                      <Text fontSize="md" whiteSpace="pre-wrap">{patient.previous_surgeries}</Text>
                    </Box>
                  )}
                  {patient.previous_cosmetic_procedures && (
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                        Previous Cosmetic Procedures
                      </Text>
                      <Text fontSize="md" whiteSpace="pre-wrap">{patient.previous_cosmetic_procedures}</Text>
                    </Box>
                  )}
                  {patient.medical_notes && (
                    <Box>
                      <Text fontWeight="bold" fontSize="sm" color="gray.600" mb={1}>
                        Medical Notes
                      </Text>
                      <Text fontSize="md" whiteSpace="pre-wrap">{patient.medical_notes}</Text>
                    </Box>
                  )}
                  {!patient.allergies &&
                    !patient.current_medications &&
                    !patient.medical_conditions &&
                    !patient.previous_surgeries &&
                    !patient.previous_cosmetic_procedures &&
                    !patient.medical_notes && (
                      <Text color="gray.500" fontStyle="italic">
                        No medical history information available
                      </Text>
                    )}
                </VStack>
              </CardBody>
            </Card>
          </TabPanel>

          {/* Visits Tab */}
          <TabPanel>
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="md">Visit History</Heading>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="purple"
                onClick={handleAddVisit}
              >
                Add Visit
              </Button>
            </Flex>

            {visitsLoading ? (
              <Flex justify="center" align="center" h="200px">
                <Spinner size="lg" color="brand.purple" />
              </Flex>
            ) : visits.length === 0 ? (
              <Card>
                <CardBody textAlign="center" py={10}>
                  <Text color="gray.500" mb={4}>
                    No visits recorded yet
                  </Text>
                  <Button colorScheme="purple" onClick={handleAddVisit}>
                    Record First Visit
                  </Button>
                </CardBody>
              </Card>
            ) : (
              <Box overflowX="auto" bg="white" borderRadius="xl" boxShadow="sm">
                <Table variant="simple">
                  <Thead bg="brand.darkPurple">
                    <Tr>
                      <Th color="white">Date</Th>
                      <Th color="white">Service</Th>
                      <Th color="white">Quantity</Th>
                      <Th color="white">Photos</Th>
                      <Th color="white">Notes</Th>
                      <Th color="white">Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visits.map((visit) => (
                      <Tr key={visit.id} _hover={{ bg: 'brand.lightGray' }}>
                        <Td fontWeight="medium">{visit.visit_date}</Td>
                        <Td>
                          <Badge colorScheme="purple">Service #{visit.service_id}</Badge>
                        </Td>
                        <Td>{visit.quantity}</Td>
                        <Td>
                          <HStack spacing={2}>
                            {visit.before_image_url && (
                              <Image
                                src={getStaticFileUrl(visit.before_image_url)}
                                alt="Before"
                                boxSize="50px"
                                objectFit="cover"
                                borderRadius="md"
                                cursor="pointer"
                                border="2px solid"
                                borderColor="brand.purple"
                                onClick={() =>
                                  window.open(
                                    getStaticFileUrl(visit.before_image_url),
                                    '_blank'
                                  )
                                }
                                _hover={{ transform: 'scale(1.05)' }}
                              />
                            )}
                            {visit.after_image_url && (
                              <Image
                                src={getStaticFileUrl(visit.after_image_url)}
                                alt="After"
                                boxSize="50px"
                                objectFit="cover"
                                borderRadius="md"
                                cursor="pointer"
                                border="2px solid"
                                borderColor="green.500"
                                onClick={() =>
                                  window.open(
                                    getStaticFileUrl(visit.after_image_url),
                                    '_blank'
                                  )
                                }
                                _hover={{ transform: 'scale(1.05)' }}
                              />
                            )}
                            {!visit.before_image_url && !visit.after_image_url && (
                              <Text color="gray.400" fontSize="sm">
                                No photos
                              </Text>
                            )}
                          </HStack>
                        </Td>
                        <Td
                          maxW="200px"
                          overflow="hidden"
                          textOverflow="ellipsis"
                          whiteSpace="nowrap"
                        >
                          {visit.notes || 'N/A'}
                        </Td>
                        <Td>
                          <ButtonGroup size="sm" spacing={2}>
                            <Button
                              colorScheme="purple"
                              variant="outline"
                              onClick={() => handleEditVisit(visit)}
                            >
                              Edit
                            </Button>
                            <Button
                              colorScheme="red"
                              variant="outline"
                              onClick={() => handleDeleteVisit(visit.id)}
                            >
                              Delete
                            </Button>
                          </ButtonGroup>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            )}
          </TabPanel>

          {/* Documents Tab (Future) */}
          <TabPanel>
            <Card>
              <CardBody textAlign="center" py={10}>
                <Text color="gray.500">
                  Document management coming soon
                </Text>
              </CardBody>
            </Card>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Visit Modal */}
      <VisitModal
        isOpen={isOpen}
        onClose={onClose}
        patientId={parseInt(id)}
        patientName={`${patient.first_name} ${patient.last_name}`}
        visitToEdit={visitToEdit}
        onSave={fetchVisits}
      />
    </Box>
  );
}

export default PatientDetail;
